import { useEffect, useState } from 'react';
import { getPricing } from '@/services/api/pricing';
import { detectClientCountry } from '@/services/geo';
import type { PricingCatalog, PricingPlanCard } from '@/types/pricing';

export function PricingSection() {
  const [catalog, setCatalog] = useState<PricingCatalog | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    (async () => {
      const country = await detectClientCountry(controller.signal);
      try {
        const c = await getPricing(country || undefined);
        if (!cancelled) setCatalog(c);
      } catch {
        if (!cancelled) setError(true);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  return (
    <section id="pricing" className="border-t border-stone-subtle bg-paper-soft/60">
      <div className="mx-auto max-w-6xl px-6 py-20">
        <div className="max-w-2xl">
          <p className="text-petit font-medium uppercase tracking-[0.14em] text-teal-600">
            Pricing
          </p>
          <h2 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight-ish text-charcoal">
            Honest plans. Local where it matters.
          </h2>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-charcoal-muted">
            {catalog?.region === 'NG'
              ? 'Priced in naira for Nigerian customers. Bring your own AI keys if you want to save more.'
              : 'Flat monthly plans in USD. Bring your own AI keys and pay only the card fee.'}
          </p>
        </div>

        {error && (
          <p className="mt-10 text-center text-tiny text-charcoal-muted">
            Pricing unavailable right now — the free tier is always available.
          </p>
        )}

        {catalog && (
          <div className="mt-12 grid gap-4 md:grid-cols-3">
            {catalog.plans.map((p) => <PlanCard key={p.plan} plan={p} />)}
          </div>
        )}

        <p className="mt-8 text-center text-tiny text-charcoal-faint">
          {catalog?.region === 'NG'
            ? 'Payments handled by Paystack. Cancel anytime.'
            : 'Payments handled by Polar (Merchant of Record). Cancel anytime.'}
        </p>
      </div>
    </section>
  );
}

function PlanCard({ plan }: { plan: PricingPlanCard }) {
  const highlight = plan.recommended;
  return (
    <div
      className={`relative flex flex-col rounded-xl border p-6 transition-colors ${
        highlight
          ? 'border-teal-500/40 bg-paper-raised shadow-paper'
          : 'border-stone-subtle bg-paper-raised hover:border-stone-default'
      }`}
    >
      {highlight && (
        <span className="absolute -top-2.5 left-6 rounded-full bg-teal-500 px-2 py-0.5 text-micro font-medium uppercase tracking-wider text-paper">
          Recommended
        </span>
      )}
      <div className="flex items-baseline justify-between">
        <h3 className="text-base font-semibold text-charcoal">{plan.name}</h3>
        <span className="text-tiny uppercase tracking-wider text-charcoal-faint">
          {plan.currency || ''}
        </span>
      </div>
      <p className="mt-1 text-petit text-charcoal-muted">{plan.tagline}</p>

      <div className="mt-5 flex items-end gap-1">
        <span className="text-3xl font-semibold text-charcoal">{plan.amount_major}</span>
        {plan.amount_minor > 0 && (
          <span className="pb-1 text-tiny text-charcoal-faint">/ {plan.period}</span>
        )}
      </div>

      <p className="mt-3 text-tiny text-charcoal-muted">
        {plan.daily_limit.toLocaleString()} AI generations per account
      </p>

      <ul className="mt-5 space-y-2.5 text-tiny text-charcoal-soft">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-teal-500" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-6">
        <a
          href="/signup"
          className={`block w-full rounded-lg px-3 py-2 text-center text-tiny font-medium transition-colors ${
            highlight
              ? 'bg-charcoal text-paper hover:bg-charcoal-soft'
              : 'border border-stone-default text-charcoal-soft hover:border-stone-strong'
          }`}
        >
          {plan.amount_minor === 0 ? 'Start free' : `Choose ${plan.name}`}
        </a>
        {plan.requires_keys && (
          <p className="mt-2 text-micro text-charcoal-faint">
            Needs your own OpenAI / Anthropic / Gemini key.
          </p>
        )}
      </div>
    </div>
  );
}
